import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import lookupService from '../services/lookupService';
import { AlternateContactType } from './models/alternate-contact-type.model';
import {
  TripLegPassengerNoteType,
  TripLegDriverNoteType,
} from './models/note.model';
import { StateCode } from './models/state-code.model';
import { TimeZone } from './models/time-zone.model';
import { VehicleType } from './models/vehicle-type.model';
import { PhoneNumberType } from './models/phone-number-type.model';
import { VehicleAttributeType } from './models/vehicle-attribute-type.model';
import {
  ClientAddressUse,
  ClientContactDepartment,
  ClientContactRole,
  ClientVertical,
} from './models/client.model';
import { TripLegOverrideReason } from './models/trip-leg-override-reason.model';
import { TripLegStatusReason } from './models/trip-leg-status-reason.model';
import { PurposeCodeCategory } from './models/purpose-code-category.model';
import {
  ChargeType,
  PricingCalculationGroup,
} from './models/pricing-model.model';
import * as uiSlice from './ui.slice';
import { PassengerPhoneUse } from './models/passenger_phone_use.model';
import { TripLegPackageStatus } from './models/trip_leg_package_status.model';
import { TransportationProviderPayStatus } from './models/transportation_provider_pay_status.model';
import { TripLegStatusItem } from './models/trip-leg-status.model';
import { FileIngestionFrequency } from './models/file-ingestion-frequency.model';
import { FileType } from './models/file-type.model';
import { AlertEvent } from './models/alert-event.model';
import {
  TaskEscalateReason,
  TaskResolveReason,
} from './models/task-reason.model';
import { Gender } from './models/gender.model';
import { IngestionCronJob } from './models/ingest-cron-job.model';
import { TripDestinationCategory } from './models/trip-destination-category';
import { EventTypeClassification } from './models/event-type-classification.model';
import { AlertType } from './models/alert-type.model';
import { ProviderCallOutcomeDetail } from './models/provider-call-outcome.model';
import { CommunicationType } from './models/communication-type.model';
import { CorrespondenceReason } from './models/correspondence-reason.model';
import { TripLegAdjustmentType } from './models/trip-leg-discussion-type.model';
import { FrequencyDayOfWeek } from './models/frequency-day-of-week.model';
import { FrequencyPeriod } from './models/frequency-period.model';
import { SmsStatus } from './models/sms-status.model';
import { TripLegPackageRejectReason } from './models/trip-leg-package-reject-reason.model';
import { PassengerAuthorizationReason } from './models/passenger-authorization-reason.model';
import { ApplicationSetting } from './models/application-setting.model';
import { InsuranceLimitType } from './models/insurance-limit-type.model';
import { DiscussionAcceptReason } from './models/discussion-accept-reason.model';
import { DiscussionDecisionOutcome } from './models/discussion-decision-outcome.model';
import {
  DiscussionRejectedReasons,
  DiscussionRejectReason,
} from './models/discussion-reject-reason.model';
import { DriverDocumentType } from './models/driver-document-type.model';
import { InsuranceStrength } from './models/insurance-strength.model';
import { InsuranceType } from './models/insurance-type.model';
import { MessagingMethod } from './models/messaging-method.model';
import { TransportationAttributeCategory } from './models/transportation-attribute-category.model';
import { TransportationProviderAddressUse } from './models/transportation-provider-address-use.model';
import { TransportationProviderDocumentType } from './models/transportation-provider-document-type.model';
import { TransportationProviderEmailUse } from './models/transportation-provider-email-use.model';
import { TransportationProviderPhoneUse } from './models/transportation-provider-phone-use.model';
import { TransportationProviderTier } from './models/transportation-provider-tier.model';
import { VehicleColor } from './models/vehicle-color.model';
import { VehicleDocumentType } from './models/vehicle-document-type.model';
import { BenefitPurpose } from './models/benefit-purpose.model';
import { LegalEntityStatus } from './models/legal-entity-status.model';
import { LegalEntityType } from './models/legal-entity-type.model';
import { LineOfBusiness } from './models/line-of-business.model';
import { ApplicationTitle } from './models/application-title.model';
import { UserCategoryType } from './models/user-category-type.model';
import {
  PermissionAction,
  PermissionResource,
  PermissionScope,
} from './models/permission.model';
import { StoreStatus } from './types';
import { TripMode } from './models/trip-mode.model';
import { PassengerAddressUse } from './models/passenger_address_use.model';
import { PassengerProfileAttributeType } from './models/passenger-profile-attribute-type.model';
import { TransportationType } from './models/transportation-type.model';
import { PaymentType } from './models/payment-type.model';
import { ClientSettingOption } from './models/client-settings.model';
import { PassengerCareLevel } from './models/passenger-care-level.model';
import { ProviderInvoiceLineItemReviewReason } from './models/provider-invoice-line-item-review-reason.model';
import { SmartLabel } from './models/smart-label.model';
import { TripLegException } from './models/trip-leg-exception.model';
import { CallOutcomeRejectReason } from './models/call-outcome-reject-reason.model';
import {
  PurposeCodeModelType,
  PurposeCodeType,
} from './models/purpose-code-model.model';

interface LookupState {
  status: StoreStatus;

  // Per-table fetch status, keyed by lookup table name. Used to dedupe the
  // fan-out of `fetchLookupItemsAsync` dispatches across the app: a table that
  // is already loading or loaded is not re-requested. `status` above stays a
  // single global value because the admin Lookups screen keys its create/update
  // flow off it.
  tableStatus: { [tableName: string]: StoreStatus };

  // Lookups
  alertEventItems: AlertEvent[];
  alertEventItemsUnfiltered: AlertEvent[];
  alertTypeItems: AlertType[];
  alertTypeItemsUnfiltered: AlertType[];
  alternateContactTypeItems: AlternateContactType[];
  alternateContactTypeItemsUnfiltered: AlternateContactType[];
  applicationSettingItems: ApplicationSetting[];
  applicationSettingItemsUnfiltered: ApplicationSetting[];
  applicationTitleItems: ApplicationTitle[];
  applicationTitleItemsUnfiltered: ApplicationTitle[];
  benefitPurposeItems: BenefitPurpose[];
  benefitPurposeItemsUnfiltered: BenefitPurpose[];
  calculationTypeItems: any[];
  calculationTypeItemsUnfiltered: any[];
  callOutcomeItems: ProviderCallOutcomeDetail[];
  callOutcomeItemsUnfiltered: ProviderCallOutcomeDetail[];
  callOutcomeRejectReasonItems: CallOutcomeRejectReason[];
  callOutcomeRejectReasonItemsUnfiltered: CallOutcomeRejectReason[];
  chargeDescriptionItems: any[];
  chargeDescriptionItemsUnfiltered: any[];
  chargeTypeItems: ChargeType[];
  chargeTypeItemsUnfiltered: ChargeType[];
  clientAddressUseItems: ClientAddressUse[];
  clientAddressUseItemsUnfiltered: ClientAddressUse[];
  clientContactDepartmentItems: ClientContactDepartment[];
  clientContactDepartmentItemsUnfiltered: ClientContactDepartment[];
  clientContactRoleItems: ClientContactRole[];
  clientContactRoleItemsUnfiltered: ClientContactRole[];
  clientSettingOptionItems: ClientSettingOption[];
  clientSettingOptionItemsUnfiltered: ClientSettingOption[];
  clientVerticalItems: ClientVertical[];
  clientVerticalItemsUnfiltered: ClientVertical[];
  communicationTypeItems: CommunicationType[];
  communicationTypeItemsUnfiltered: CommunicationType[];
  correspondenceReasonItems: CorrespondenceReason[];
  correspondenceReasonItemsUnfiltered: CorrespondenceReason[];
  tripLegAdjustmentTypeItems: TripLegAdjustmentType[];
  tripLegAdjustmentTypeItemsUnfiltered: TripLegAdjustmentType[];
  discussionAcceptReasonItems: DiscussionAcceptReason[];
  discussionAcceptReasonItemsUnfiltered: DiscussionAcceptReason[];
  discussionDecisionOutcomeItems: DiscussionDecisionOutcome[];
  discussionDecisionOutcomeItemsUnfiltered: DiscussionDecisionOutcome[];
  discussionRejectReasonItems: DiscussionRejectReason[];
  discussionRejectedReasonsItems: DiscussionRejectedReasons[];
  discussionRejectReasonItemsUnfiltered: DiscussionRejectReason[];
  driverDocumentTypeItems: DriverDocumentType[];
  driverDocumentTypeItemsUnfiltered: DriverDocumentType[];
  eventTypeClassificationItemsUnfiltered: EventTypeClassification[];
  fileIngestionFrequencyItems: FileIngestionFrequency[];
  fileIngestionFrequencyItemsUnfiltered: FileIngestionFrequency[];
  fileTypeItems: FileType[];
  fileTypeItemsUnfiltered: FileType[];
  frequencyDayOfWeekItems: FrequencyDayOfWeek[];
  frequencyDayOfWeekItemsUnfiltered: FrequencyDayOfWeek[];
  frequencyPeriodItems: FrequencyPeriod[];
  frequencyPeriodItemsUnfiltered: FrequencyPeriod[];
  genderItems: Gender[];
  genderItemsUnfiltered: Gender[];
  ingestionCronJobItems: IngestionCronJob[];
  ingestionCronJobItemsUnfiltered: IngestionCronJob[];
  insuranceLimitTypeItems: InsuranceLimitType[];
  insuranceLimitTypeItemsUnfiltered: InsuranceLimitType[];
  insuranceStrengthItems: InsuranceStrength[];
  insuranceStrengthItemsUnfiltered: InsuranceStrength[];
  insuranceTypeItems: InsuranceType[];
  insuranceTypeItemsUnfiltered: InsuranceType[];
  legalEntityStatusItemsUnfiltered: LegalEntityStatus[];
  legalEntityTypeItemsUnfiltered: LegalEntityType[];
  lineOfBusinessItems: LineOfBusiness[];
  lineOfBusinessItemsUnfiltered: LineOfBusiness[];
  messagingMethodItems: MessagingMethod[];
  messagingMethodItemsUnfiltered: MessagingMethod[];
  passengerAuthorizationReasonItems: PassengerAuthorizationReason[];
  passengerAuthorizationReasonItemsUnfiltered: PassengerAuthorizationReason[];
  passengerCareLevelItems: PassengerCareLevel[];
  passengerCareLevelItemsUnfiltered: PassengerCareLevel[];
  passengerPhoneUseItems: PassengerPhoneUse[];
  passengerPhoneUseItemsUnfiltered: PassengerPhoneUse[];
  passengerAddressUseItems: PassengerAddressUse[];
  passengerAddressUseItemsUnfiltered: PassengerAddressUse[];
  passengerProfileAttributeTypeItems: PassengerProfileAttributeType[];
  passengerProfileAttributeTypeItemsUnfiltered: PassengerProfileAttributeType[];
  paymentTypeItems: PaymentType[];
  paymentTypeItemsUnfiltered: PaymentType[];
  permissionResourceItems: PermissionResource[];
  permissionResourceItemsUnfiltered: PermissionResource[];
  permissionActionItems: PermissionAction[];
  permissionActionItemsUnfiltered: PermissionAction[];
  permissionScopeItems: PermissionScope[];
  permissionScopeItemsUnfiltered: PermissionScope[];
  phoneNumberTypeItems: PhoneNumberType[];
  phoneNumberTypeItemsUnfiltered: PhoneNumberType[];
  pricingCalculationGroupItems: PricingCalculationGroup[];
  pricingCalculationGroupItemsUnfiltered: PricingCalculationGroup[];
  purposeCodeCategoryItems: PurposeCodeCategory[];
  purposeCodeCategoryItemsUnfiltered: PurposeCodeCategory[];
  purposeCodeModelTypeItems: PurposeCodeModelType[];
  purposeCodeModelTypeItemsUnfiltered: PurposeCodeModelType[];
  purposeCodeTypeItems: PurposeCodeType[];
  purposeCodeTypeItemsUnfiltered: PurposeCodeType[];
  providerInvoiceLineItemReviewReasonItems: ProviderInvoiceLineItemReviewReason[];
  providerInvoiceLineItemReviewReasonItemsUnfiltered: ProviderInvoiceLineItemReviewReason[];
  smartLabelItems: SmartLabel[];
  smsStatusItems: SmsStatus[];
  smsStatusItemsUnfiltered: SmsStatus[];
  stateCodeListItems: StateCode[];
  stateCodeListItemsUnfiltered: StateCode[];
  taskEscalateReasonItems: TaskEscalateReason[];
  taskEscalateReasonItemsUnfiltered: TaskEscalateReason[];
  taskResolveReasonItems: TaskResolveReason[];
  taskResolveReasonItemsUnfiltered: TaskResolveReason[];
  timeZoneItems: TimeZone[];
  timeZoneItemsUnfiltered: TimeZone[];
  transportationAttributeMap: { [key: string]: string };
  reverseTransportationAttributeMap: { [key: string]: string };
  transportationAttributeCategoryItems: TransportationAttributeCategory[];
  transportationAttributeCategoryItemsUnfiltered: TransportationAttributeCategory[];
  transportationProviderAddressUseItems: TransportationProviderAddressUse[];
  transportationProviderAddressUseItemsUnfiltered: TransportationProviderAddressUse[];
  transportationProviderDocumentTypeItems: TransportationProviderDocumentType[];
  transportationProviderDocumentTypeItemsUnfiltered: TransportationProviderDocumentType[];
  transportationProviderEmailUseItems: TransportationProviderEmailUse[];
  transportationProviderEmailUseItemsUnfiltered: TransportationProviderEmailUse[];
  transportationProviderPayStatusItems: TransportationProviderPayStatus[];
  transportationProviderPayStatusItemsUnfiltered: TransportationProviderPayStatus[];
  transportationProviderPhoneUseItems: TransportationProviderPhoneUse[];
  transportationProviderPhoneUseItemsUnfiltered: TransportationProviderPhoneUse[];
  transportationProviderTierItems: TransportationProviderTier[];
  transportationProviderTierItemsUnfiltered: TransportationProviderTier[];
  transportationTypeItems: TransportationType[];
  transportationTypeItemsUnfiltered: TransportationType[];
  tripDestinationCategoryItems: TripDestinationCategory[];
  tripDestinationCategoryItemsUnfiltered: TripDestinationCategory[];
  tripLegDriverNoteTypeItems: TripLegDriverNoteType[];
  tripLegDriverNoteTypeItemsUnfiltered: TripLegDriverNoteType[];
  tripLegExceptionItems: TripLegException[];
  tripLegExceptionItemsUnfiltered: TripLegException[];
  tripLegOverrideReasonItems: TripLegOverrideReason[];
  tripLegOverrideReasonItemsUnfiltered: TripLegOverrideReason[];
  tripLegPackageRejectReasonItems: TripLegPackageRejectReason[];
  tripLegPackageRejectReasonItemsUnfiltered: TripLegPackageRejectReason[];
  tripLegPackageStatusItems: TripLegPackageStatus[];
  tripLegPackageStatusItemsUnfiltered: TripLegPackageStatus[];
  tripLegPassengerNoteTypeItems: TripLegPassengerNoteType[];
  tripLegPassengerNoteTypeItemsUnfiltered: TripLegPassengerNoteType[];
  tripLegStatusItems: TripLegStatusItem[];
  tripLegStatusItemsUnfiltered: TripLegStatusItem[];
  tripLegStatusReasonItems: TripLegStatusReason[];
  tripLegStatusReasonItemsUnfiltered: TripLegStatusReason[];
  tripModeItems: TripMode[];
  tripModeItemsUnfiltered: TripMode[];
  userCategoryTypeItems: UserCategoryType[];
  userCategoryTypeItemsUnfiltered: UserCategoryType[];
  vehicleAttributeTypeItems: VehicleAttributeType[];
  vehicleAttributeTypeItemsUnfiltered: VehicleAttributeType[];
  vehicleColorItems: VehicleColor[];
  vehicleColorItemsUnfiltered: VehicleColor[];
  vehicleDocumentTypeItems: VehicleDocumentType[];
  vehicleDocumentTypeItemsUnfiltered: VehicleDocumentType[];
  vehicleTypeItems: VehicleType[];
  vehicleTypeItemsUnfiltered: VehicleType[];
  lookupsApiInfo: any;
}

type LookupItemsKey = Extract<keyof LookupState, `${string}Items`>;
type LookupTableName = LookupItemsKey extends `${infer Name}Items`
  ? Name
  : never;

export const initialState: LookupState = {
  status: StoreStatus.Idle,
  tableStatus: {},
  alertEventItems: [],
  alertEventItemsUnfiltered: [],
  alertTypeItems: [],
  alertTypeItemsUnfiltered: [],
  alternateContactTypeItems: [],
  alternateContactTypeItemsUnfiltered: [],
  applicationSettingItems: [],
  applicationSettingItemsUnfiltered: [],
  applicationTitleItems: [],
  applicationTitleItemsUnfiltered: [],
  benefitPurposeItems: [],
  benefitPurposeItemsUnfiltered: [],
  calculationTypeItems: [],
  calculationTypeItemsUnfiltered: [],
  callOutcomeItems: [],
  callOutcomeItemsUnfiltered: [],
  callOutcomeRejectReasonItems: [],
  callOutcomeRejectReasonItemsUnfiltered: [],
  chargeDescriptionItems: [],
  chargeDescriptionItemsUnfiltered: [],
  chargeTypeItems: [],
  chargeTypeItemsUnfiltered: [],
  clientAddressUseItems: [],
  clientAddressUseItemsUnfiltered: [],
  clientContactDepartmentItems: [],
  clientContactDepartmentItemsUnfiltered: [],
  clientContactRoleItems: [],
  clientContactRoleItemsUnfiltered: [],
  clientSettingOptionItems: [],
  clientSettingOptionItemsUnfiltered: [],
  clientVerticalItems: [],
  clientVerticalItemsUnfiltered: [],
  communicationTypeItems: [],
  communicationTypeItemsUnfiltered: [],
  correspondenceReasonItems: [],
  correspondenceReasonItemsUnfiltered: [],
  tripLegAdjustmentTypeItems: [],
  tripLegAdjustmentTypeItemsUnfiltered: [],
  discussionAcceptReasonItems: [],
  discussionAcceptReasonItemsUnfiltered: [],
  discussionDecisionOutcomeItems: [],
  discussionDecisionOutcomeItemsUnfiltered: [],
  discussionRejectReasonItems: [],
  discussionRejectedReasonsItems: [],
  discussionRejectReasonItemsUnfiltered: [],
  driverDocumentTypeItems: [],
  driverDocumentTypeItemsUnfiltered: [],
  eventTypeClassificationItemsUnfiltered: [],
  fileIngestionFrequencyItems: [],
  fileIngestionFrequencyItemsUnfiltered: [],
  fileTypeItems: [],
  fileTypeItemsUnfiltered: [],
  frequencyDayOfWeekItems: [],
  frequencyDayOfWeekItemsUnfiltered: [],
  frequencyPeriodItems: [],
  frequencyPeriodItemsUnfiltered: [],
  genderItems: [],
  genderItemsUnfiltered: [],
  ingestionCronJobItems: [],
  ingestionCronJobItemsUnfiltered: [],
  insuranceLimitTypeItems: [],
  insuranceLimitTypeItemsUnfiltered: [],
  insuranceStrengthItems: [],
  insuranceStrengthItemsUnfiltered: [],
  insuranceTypeItems: [],
  insuranceTypeItemsUnfiltered: [],
  legalEntityStatusItemsUnfiltered: [],
  legalEntityTypeItemsUnfiltered: [],
  lineOfBusinessItems: [],
  lineOfBusinessItemsUnfiltered: [],
  messagingMethodItems: [],
  messagingMethodItemsUnfiltered: [],
  passengerAuthorizationReasonItems: [],
  passengerAuthorizationReasonItemsUnfiltered: [],
  passengerCareLevelItems: [],
  passengerCareLevelItemsUnfiltered: [],
  passengerPhoneUseItems: [],
  passengerPhoneUseItemsUnfiltered: [],
  passengerAddressUseItems: [],
  passengerAddressUseItemsUnfiltered: [],
  passengerProfileAttributeTypeItems: [],
  passengerProfileAttributeTypeItemsUnfiltered: [],
  paymentTypeItems: [],
  paymentTypeItemsUnfiltered: [],
  permissionResourceItems: [],
  permissionResourceItemsUnfiltered: [],
  permissionActionItems: [],
  permissionActionItemsUnfiltered: [],
  permissionScopeItems: [],
  permissionScopeItemsUnfiltered: [],
  phoneNumberTypeItems: [],
  phoneNumberTypeItemsUnfiltered: [],
  pricingCalculationGroupItems: [],
  pricingCalculationGroupItemsUnfiltered: [],
  purposeCodeCategoryItems: [],
  purposeCodeCategoryItemsUnfiltered: [],
  purposeCodeModelTypeItems: [],
  purposeCodeModelTypeItemsUnfiltered: [],
  purposeCodeTypeItems: [],
  purposeCodeTypeItemsUnfiltered: [],
  providerInvoiceLineItemReviewReasonItems: [],
  providerInvoiceLineItemReviewReasonItemsUnfiltered: [],
  smartLabelItems: [],
  smsStatusItems: [],
  smsStatusItemsUnfiltered: [],
  stateCodeListItems: [],
  stateCodeListItemsUnfiltered: [],
  taskEscalateReasonItems: [],
  taskEscalateReasonItemsUnfiltered: [],
  taskResolveReasonItems: [],
  taskResolveReasonItemsUnfiltered: [],
  timeZoneItems: [],
  timeZoneItemsUnfiltered: [],
  transportationAttributeMap: {},
  reverseTransportationAttributeMap: {},
  transportationAttributeCategoryItems: [],
  transportationAttributeCategoryItemsUnfiltered: [],
  transportationProviderAddressUseItems: [],
  transportationProviderAddressUseItemsUnfiltered: [],
  transportationProviderDocumentTypeItems: [],
  transportationProviderDocumentTypeItemsUnfiltered: [],
  transportationProviderEmailUseItems: [],
  transportationProviderEmailUseItemsUnfiltered: [],
  transportationProviderPayStatusItems: [],
  transportationProviderPayStatusItemsUnfiltered: [],
  transportationProviderPhoneUseItems: [],
  transportationProviderPhoneUseItemsUnfiltered: [],
  transportationProviderTierItems: [],
  transportationProviderTierItemsUnfiltered: [],
  transportationTypeItems: [],
  transportationTypeItemsUnfiltered: [],
  tripDestinationCategoryItems: [],
  tripDestinationCategoryItemsUnfiltered: [],
  tripLegDriverNoteTypeItems: [],
  tripLegDriverNoteTypeItemsUnfiltered: [],
  tripLegExceptionItems: [],
  tripLegExceptionItemsUnfiltered: [],
  tripLegOverrideReasonItems: [],
  tripLegOverrideReasonItemsUnfiltered: [],
  tripLegPackageRejectReasonItems: [],
  tripLegPackageRejectReasonItemsUnfiltered: [],
  tripLegPackageStatusItems: [],
  tripLegPackageStatusItemsUnfiltered: [],
  tripLegPassengerNoteTypeItems: [],
  tripLegPassengerNoteTypeItemsUnfiltered: [],
  tripLegStatusItems: [],
  tripLegStatusItemsUnfiltered: [],
  tripLegStatusReasonItems: [],
  tripLegStatusReasonItemsUnfiltered: [],
  tripModeItems: [],
  tripModeItemsUnfiltered: [],
  userCategoryTypeItems: [],
  userCategoryTypeItemsUnfiltered: [],
  vehicleAttributeTypeItems: [],
  vehicleAttributeTypeItemsUnfiltered: [],
  vehicleColorItems: [],
  vehicleColorItemsUnfiltered: [],
  vehicleDocumentTypeItems: [],
  vehicleDocumentTypeItemsUnfiltered: [],
  vehicleTypeItems: [],
  vehicleTypeItemsUnfiltered: [],
  lookupsApiInfo: null,
};

export const slice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setStatus: (state: LookupState, action: { payload: StoreStatus }) => {
      state.status = action.payload;
    },
    setTableStatus: (
      state: LookupState,
      action: PayloadAction<{ tableName: string; status: StoreStatus }>,
    ) => {
      state.tableStatus[action.payload.tableName] = action.payload.status;
    },
    invalidateLookupTable: (
      state: LookupState,
      action: PayloadAction<string>,
    ) => {
      delete state.tableStatus[action.payload];
    },
    setlookupsApiInfo: (state, action: PayloadAction<object>) => {
      state.lookupsApiInfo = action.payload;
    },
    setLookupItems: (
      state: LookupState,
      action: { payload: { tableName: string; items: any[] } },
    ) => {
      const tableName = action.payload.tableName;
      const items =
        tableName === 'stateCodeList'
          ? action.payload.items.sort((a, b) =>
              a.stateName.localeCompare(b.stateName),
            )
          : action.payload.items;
      (state as any)[`${tableName}Items`] = items.filter((l) => l.isActive);
      (state as any)[`${tableName}ItemsUnfiltered`] = items;
    },
    addLookupItem: (
      state: LookupState,
      action: { payload: { tableName: string; item: any } },
    ) => {
      (state as any)[`${action.payload.tableName}ItemsUnfiltered`].push(
        action.payload.item,
      );
      if (action.payload.item.isActive) {
        (state as any)[`${action.payload.tableName}Items`].push(
          action.payload.item,
        );
      }
    },
    updateLookupItem: (
      state: LookupState,
      action: { payload: { tableName: string; item: any } },
    ) => {
      const idName = `${action.payload.tableName}ID`;
      const unfilteredItems = (state as any)[
        `${action.payload.tableName}ItemsUnfiltered`
      ];
      const unfilteredIndex = unfilteredItems.findIndex(
        (i: any) => i[idName] === action.payload.item[idName],
      );
      if (unfilteredIndex >= 0) {
        (state as any)[`${action.payload.tableName}ItemsUnfiltered`].splice(
          unfilteredIndex,
          1,
          action.payload.item,
        );
      }

      const items = (state as any)[`${action.payload.tableName}Items`];
      const idx = items.findIndex(
        (i: any) => i[idName] === action.payload.item[idName],
      );
      if (action.payload.item.isActive && idx < 0) {
        (state as any)[`${action.payload.tableName}Items`].splice(
          unfilteredIndex,
          1,
          action.payload.item,
        );
      } else if (!action.payload.item.isActive && idx >= 0) {
        (state as any)[`${action.payload.tableName}Items`].splice(idx, 1);
      }
    },
    setTransportationAttributeMap: (
      state: LookupState,
      action: { payload: { [key: string]: string } },
    ) => {
      state.transportationAttributeMap = action.payload;
    },
    setReverseTransportationAttributeMap: (
      state: LookupState,
      action: { payload: { [key: string]: string } },
    ) => {
      state.reverseTransportationAttributeMap = action.payload;
    },
  },
});

export const { setStatus } = slice.actions;
export const { setTableStatus, invalidateLookupTable } = slice.actions;
export const { setLookupItems } = slice.actions;
export const { addLookupItem } = slice.actions;
export const { updateLookupItem } = slice.actions;
export const { setlookupsApiInfo } = slice.actions;
export const {
  setTransportationAttributeMap,
  setReverseTransportationAttributeMap,
} = slice.actions;

export const fetchLookupsApiInfoAsync = () => (dispatch: Function) => {
  lookupService.getLookupsApiInfo().subscribe({
    next: (apiInfo: any) => {
      dispatch(setlookupsApiInfo(apiInfo));
    },
    error: (err) => {
      uiSlice.setErrorMessage(err.message || 'snackbar.apiVersionError');
    },
  });
};

/**
 * Lookup tables are reference data that changes rarely, but every screen that
 * needs one dispatches this on mount — ~28 requests during app boot alone, each
 * with its own CORS preflight. A table already loading or loaded is served from
 * the store instead of re-requested. Pass `{ force: true }` to bypass the cache
 * (e.g. after an edit, or to revalidate).
 */
export const fetchLookupItemsAsync =
  (tableName: string, options?: { force?: boolean }) =>
  (dispatch: Function, getState: () => { lookup: LookupState }) => {
    // Optional chaining: the cache is an optimization, so a partially-built
    // store (as in tests) should fall through to fetching, not crash.
    const cached = getState().lookup?.tableStatus?.[tableName];
    const isCached =
      cached === StoreStatus.Loading || cached === StoreStatus.Loaded;
    if (isCached && !options?.force) {
      return;
    }

    dispatch(setTableStatus({ tableName, status: StoreStatus.Loading }));
    dispatch(setStatus(StoreStatus.Loading));
    lookupService.getLookupItems(tableName).subscribe({
      next: (items) => {
        dispatch(
          setLookupItems({
            tableName,
            items,
          }),
        );
        dispatch(setTableStatus({ tableName, status: StoreStatus.Loaded }));
        dispatch(setStatus(StoreStatus.Loaded));
      },
      error: (err) => {
        // Leave the table un-cached so a later mount can retry.
        dispatch(setTableStatus({ tableName, status: StoreStatus.Error }));
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setStatus(StoreStatus.Error));
      },
    });
  };

export const createLookupItemAsync =
  (tableName: string, item: any) => (dispatch: Function) => {
    dispatch(setStatus(StoreStatus.Creating));
    lookupService.createLookupItem(tableName, item).subscribe({
      next: (newItem) => {
        dispatch(
          addLookupItem({
            tableName,
            item: newItem,
          }),
        );
        // The reducer keeps the in-memory copy correct, but drop the cache
        // entry so the next mount revalidates against the server.
        dispatch(invalidateLookupTable(tableName));
        dispatch(setStatus(StoreStatus.Success));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.lookupRecordCreatedSuccess'),
        );
      },
      error: (err) => {
        if (err.message?.includes('must be unique')) {
          dispatch(uiSlice.setErrorMessage('snackbar.bcInUse'));
        } else {
          dispatch(uiSlice.setErrorMessage(err.message));
        }
        dispatch(setStatus(StoreStatus.Error));
      },
    });
  };

export const updateLookupItemAsync =
  (tableName: string, item: any) => (dispatch: Function) => {
    lookupService.updateLookupItem(tableName, item).subscribe({
      next: (newItem) => {
        dispatch(
          updateLookupItem({
            tableName,
            item: newItem,
          }),
        );
        dispatch(invalidateLookupTable(tableName));
        dispatch(setStatus(StoreStatus.Success));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.lookupRecordUpdatedSuccess'),
        );
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setStatus(StoreStatus.Error));
      },
    });
  };

const TRANSPORTATION_ATTRIBUTE_TABLE = 'transportationAttribute';

export const fetchTransportationAttributeMapAsync =
  (options?: { force?: boolean }) =>
  (dispatch: Function, getState: () => { lookup: LookupState }) => {
    const cached =
      getState().lookup?.tableStatus?.[TRANSPORTATION_ATTRIBUTE_TABLE];
    const isCached =
      cached === StoreStatus.Loading || cached === StoreStatus.Loaded;
    if (isCached && !options?.force) {
      return;
    }

    dispatch(
      setTableStatus({
        tableName: TRANSPORTATION_ATTRIBUTE_TABLE,
        status: StoreStatus.Loading,
      }),
    );
    lookupService.getLookupItems(TRANSPORTATION_ATTRIBUTE_TABLE).subscribe({
      next: (items) => {
        const map = items.reduce((cur, obj) => {
          cur[obj.transportationAttributeUUID] = obj.transportationAttributeBC;
          return cur;
        }, {});
        const reverseMap = items.reduce((cur, obj) => {
          cur[obj.transportationAttributeBC] = obj.transportationAttributeUUID;
          return cur;
        }, {});
        dispatch(setReverseTransportationAttributeMap(reverseMap));
        dispatch(setTransportationAttributeMap(map));
        dispatch(
          setTableStatus({
            tableName: TRANSPORTATION_ATTRIBUTE_TABLE,
            status: StoreStatus.Loaded,
          }),
        );
      },
      // This fetch has never surfaced errors to the user; keep it that way, but
      // clear the cache entry so a later mount can retry.
      error: () => {
        dispatch(
          setTableStatus({
            tableName: TRANSPORTATION_ATTRIBUTE_TABLE,
            status: StoreStatus.Error,
          }),
        );
      },
    });
  };

export const selectLookupStatus = (state: { lookup: LookupState }) =>
  state.lookup.status;
export const selectLookupTableStatus =
  (tableName: string) => (state: { lookup: LookupState }) =>
    state.lookup.tableStatus[tableName] ?? StoreStatus.Idle;
export const selectLookupItems =
  <T = unknown>(tableName: LookupTableName) =>
  (state: { lookup: LookupState }) =>
    (state.lookup[`${tableName}Items` as LookupItemsKey] as unknown as T[]) ??
    [];
export const selectAlertEvents = (state: { lookup: LookupState }) =>
  state.lookup.alertEventItems;
export const selectAlertEventsUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.alertEventItemsUnfiltered;
export const selectAlertTypes = (state: { lookup: LookupState }) =>
  state.lookup.alertTypeItems;
export const selectAlertTypesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.alertTypeItemsUnfiltered;
export const selectAlternateContactTypes = (state: { lookup: LookupState }) =>
  state.lookup.alternateContactTypeItems;
export const selectAlternateContactTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.alternateContactTypeItemsUnfiltered;
export const selectApplicationSettings = (state: { lookup: LookupState }) =>
  state.lookup.applicationSettingItems;
export const selectApplicationSettingsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.applicationSettingItemsUnfiltered;
export const selectApplicationTitles = (state: { lookup: LookupState }) =>
  state.lookup.applicationTitleItems;
export const selectApplicationTitlesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.applicationTitleItemsUnfiltered;
export const selectBenefitPurposes = (state: { lookup: LookupState }) =>
  state.lookup.benefitPurposeItems;
export const selectBenefitPurposesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.benefitPurposeItemsUnfiltered;
export const selectPricingCalculationGroups = (state: {
  lookup: LookupState;
}) => state.lookup.pricingCalculationGroupItems;
export const selectPricingCalculationGroupsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.pricingCalculationGroupItemsUnfiltered;
export const selectCalculationTypes = (state: { lookup: LookupState }) =>
  state.lookup.calculationTypeItems;
export const selectCalculationTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.calculationTypeItemsUnfiltered;
export const selectCallOutcomes = (state: { lookup: LookupState }) =>
  state.lookup.callOutcomeItems;
export const selectCallOutcomesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.callOutcomeItemsUnfiltered;
export const selectCallOutcomeRejectReasons = (state: {
  lookup: LookupState;
}) => state.lookup.callOutcomeRejectReasonItems;
export const selectCallOutcomeRejectReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.callOutcomeRejectReasonItemsUnfiltered;
export const selectChargeDescriptions = (state: { lookup: LookupState }) =>
  state.lookup.chargeDescriptionItems;
export const selectChargeDescriptionsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.chargeDescriptionItemsUnfiltered;
export const selectChargeTypes = (state: { lookup: LookupState }) =>
  state.lookup.chargeTypeItems;
export const selectChargeTypesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.chargeTypeItemsUnfiltered;
export const selectClientAddressUses = (state: { lookup: LookupState }) =>
  state.lookup.clientAddressUseItems;
export const selectClientAddressUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.clientAddressUseItemsUnfiltered;
export const selectClientContactDepartments = (state: {
  lookup: LookupState;
}) => state.lookup.clientContactDepartmentItems;
export const selectClientContactDepartmentsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.clientContactDepartmentItemsUnfiltered;
export const selectClientContactRoles = (state: { lookup: LookupState }) =>
  state.lookup.clientContactRoleItems;
export const selectClientContactRolesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.clientContactRoleItemsUnfiltered;
export const selectClientSettingOptions = (state: { lookup: LookupState }) =>
  state.lookup.clientSettingOptionItems;
export const selectClientSettingOptionsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.clientSettingOptionItemsUnfiltered;
export const selectClientVerticals = (state: { lookup: LookupState }) =>
  state.lookup.clientVerticalItems;
export const selectClientVerticalsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.clientVerticalItemsUnfiltered;
export const selectCommunicationTypes = (state: { lookup: LookupState }) =>
  state.lookup.communicationTypeItems;
export const selectCommunicationTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.communicationTypeItemsUnfiltered;
export const selectCorrespondenceReasons = (state: { lookup: LookupState }) =>
  state.lookup.correspondenceReasonItems;
export const selectCorrespondenceReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.correspondenceReasonItemsUnfiltered;
export const selectTripLegDiscussionTypes = (state: { lookup: LookupState }) =>
  state.lookup.tripLegAdjustmentTypeItems;
export const selectTripLegDiscussionTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegAdjustmentTypeItemsUnfiltered;
export const selectDiscussionAcceptReasons = (state: { lookup: LookupState }) =>
  state.lookup.discussionAcceptReasonItems;
export const selectDiscussionAcceptReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.discussionAcceptReasonItemsUnfiltered;
export const selectDiscussionDecisionOutcomes = (state: {
  lookup: LookupState;
}) => state.lookup.discussionDecisionOutcomeItems;
export const selectDiscussionDecisionOutcomesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.discussionDecisionOutcomeItemsUnfiltered;
export const selectDiscussionRejectReasons = (state: { lookup: LookupState }) =>
  state.lookup.discussionRejectReasonItems;
export const selectDiscussionRejectReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.discussionRejectReasonItemsUnfiltered;
export const selectDriverDocumentTypes = (state: { lookup: LookupState }) =>
  state.lookup.driverDocumentTypeItems;
export const selectDriverDocumentTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.driverDocumentTypeItemsUnfiltered;
export const selectDriverNoteTypes = (state: { lookup: LookupState }) =>
  state.lookup.tripLegDriverNoteTypeItems;
export const selectDriverNoteTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegDriverNoteTypeItemsUnfiltered;
export const selectEventTypeClassificaitonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.eventTypeClassificationItemsUnfiltered;
export const selectFileIngestionFrequenciesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.fileIngestionFrequencyItemsUnfiltered;
export const selectFileTypesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.fileTypeItemsUnfiltered;
export const selectFrequencyDayOfWeeks = (state: { lookup: LookupState }) =>
  state.lookup.frequencyDayOfWeekItems;
export const selectFrequencyDayOfWeeksUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.frequencyDayOfWeekItemsUnfiltered;
export const selectFrequencyPeriods = (state: { lookup: LookupState }) =>
  state.lookup.frequencyPeriodItems;
export const selectFrequencyPeriodsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.frequencyPeriodItemsUnfiltered;
export const selectGenders = (state: { lookup: LookupState }) =>
  state.lookup.genderItems;
export const selectGendersUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.genderItemsUnfiltered;
export const selectIngestionCronJobs = (state: { lookup: LookupState }) =>
  state.lookup.ingestionCronJobItems;
export const selectIngestionCronJobsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.ingestionCronJobItemsUnfiltered;
export const selectInsuranceLimitTypes = (state: { lookup: LookupState }) =>
  state.lookup.insuranceLimitTypeItems;
export const selectInsuranceLimitTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.insuranceLimitTypeItemsUnfiltered;
export const selectInsuranceStrengths = (state: { lookup: LookupState }) =>
  state.lookup.insuranceStrengthItems;
export const selectInsuranceStrengthsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.insuranceStrengthItemsUnfiltered;
export const selectInsuranceTypes = (state: { lookup: LookupState }) =>
  state.lookup.insuranceTypeItems;
export const selectInsuranceTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.insuranceTypeItemsUnfiltered;
export const selectLegalEntityStatusUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.legalEntityStatusItemsUnfiltered;
export const selectLegalEntityTypeUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.legalEntityTypeItemsUnfiltered;
export const selectLinesOfBusiness = (state: { lookup: LookupState }) =>
  state.lookup.lineOfBusinessItems;
export const selectLinesOfBusinessUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.lineOfBusinessItemsUnfiltered;
export const selectMessagingMethods = (state: { lookup: LookupState }) =>
  state.lookup.messagingMethodItems;
export const selectMessagingMethodsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.messagingMethodItemsUnfiltered;
export const selectOverrideAuthorizationReasons = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegOverrideReasonItems;
export const selectOverrideAuthorizationReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegOverrideReasonItemsUnfiltered;
export const selectPassengerAuthorizationReasons = (state: {
  lookup: LookupState;
}) => state.lookup.passengerAuthorizationReasonItems;
export const selectPassengerAuthorizationReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.passengerAuthorizationReasonItemsUnfiltered;
export const selectPassengerNoteTypes = (state: { lookup: LookupState }) =>
  state.lookup.tripLegPassengerNoteTypeItems;
export const selectPassengerNoteTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegPassengerNoteTypeItemsUnfiltered;
export const selectPaymentTypes = (state: { lookup: LookupState }) =>
  state.lookup.paymentTypeItems;
export const selectPaymentTypesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.paymentTypeItemsUnfiltered;
export const selectPermissionResources = (state: { lookup: LookupState }) =>
  state.lookup.permissionResourceItems;
export const selectPermissionResourcesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.permissionResourceItemsUnfiltered;
export const selectPermissionActions = (state: { lookup: LookupState }) =>
  state.lookup.permissionActionItems;
export const selectPermissionActionsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.permissionActionItemsUnfiltered;
export const selectPermissionScopes = (state: { lookup: LookupState }) =>
  state.lookup.permissionScopeItems;
export const selectPermissionScopesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.permissionScopeItemsUnfiltered;
export const selectPassengerPhoneUses = (state: { lookup: LookupState }) =>
  state.lookup.passengerPhoneUseItems;
export const selectPassengerPhoneUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.passengerPhoneUseItemsUnfiltered;
export const selectPassengerAddressUses = (state: { lookup: LookupState }) =>
  state.lookup.passengerAddressUseItems;
export const selectPassengerAddressUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.passengerAddressUseItemsUnfiltered;
export const selectPassengerProfileAttributeTypes = (state: {
  lookup: LookupState;
}) => state.lookup.passengerProfileAttributeTypeItems;
export const selectPassengerProfileAttributeTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.passengerProfileAttributeTypeItemsUnfiltered;
export const selectPhoneNumberTypes = (state: { lookup: LookupState }) =>
  state.lookup.phoneNumberTypeItems;
export const selectPhoneNumberTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.phoneNumberTypeItemsUnfiltered;
const selectSmartLabelItems = (state: { lookup: LookupState }) =>
  state.lookup.smartLabelItems;
export const selectCommsCenterSmartLabels = createSelector(
  [selectSmartLabelItems],
  (smartLabelItems) =>
    smartLabelItems.filter(
      (smartLabel) => smartLabel.smartLabelGroupBC === 'COMMSCENTER',
    ),
);
export const selectTransportationAttributeCategories = (state: {
  lookup: LookupState;
}) => state.lookup.transportationAttributeCategoryItems;
export const selectTransportationAttributeCategoriesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationAttributeCategoryItemsUnfiltered;
export const selectTransportationProviderAddressUses = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderAddressUseItems;
export const selectTransportationProviderAddressUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderAddressUseItemsUnfiltered;
export const selectTransportationProviderDocumentTypes = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderDocumentTypeItems;
export const selectTransportationProviderDocumentTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderDocumentTypeItemsUnfiltered;
export const selectTransportationProviderEmailUses = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderEmailUseItems;
export const selectTransportationProviderEmailUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderEmailUseItemsUnfiltered;
export const selectTransportationProviderPhoneUses = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderPhoneUseItems;
export const selectTransportationProviderPhoneUsesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderPhoneUseItemsUnfiltered;
export const selectTransportationProviderTiers = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderTierItems;
export const selectTransportationProviderTiersUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderTierItemsUnfiltered;
export const selectTransportationTypes = (state: { lookup: LookupState }) =>
  state.lookup.transportationTypeItems;
export const selectTransportationTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationTypeItemsUnfiltered;
export const selectPassengerCareLevels = (state: { lookup: LookupState }) =>
  state.lookup.passengerCareLevelItems;
export const selectPassengerCareLevelsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.passengerCareLevelItemsUnfiltered;
export const selectPurposeCodeCategories = (state: { lookup: LookupState }) =>
  state.lookup.purposeCodeCategoryItems;
export const selectPurposeCodeCategoriesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.purposeCodeCategoryItemsUnfiltered;
export const selectPurposeCodeModelTypes = (state: { lookup: LookupState }) =>
  state.lookup.purposeCodeModelTypeItems;
export const selectPurposeCodeModelTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.purposeCodeModelTypeItemsUnfiltered;
export const selectPurposeCodeTypes = (state: { lookup: LookupState }) =>
  state.lookup.purposeCodeTypeItems;
export const selectPurposeCodeTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.purposeCodeTypeItemsUnfiltered;
export const selectProviderInvoiceLineItemReviewReasons = (state: {
  lookup: LookupState;
}) => state.lookup.providerInvoiceLineItemReviewReasonItems;
export const selectProviderInvoiceLineItemReviewReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.providerInvoiceLineItemReviewReasonItemsUnfiltered;
export const selectSmsStatuses = (state: { lookup: LookupState }) =>
  state.lookup.smsStatusItems;
export const selectSmsStatusesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.smsStatusItemsUnfiltered;
export const selectStateCodes = (state: { lookup: LookupState }) =>
  state.lookup.stateCodeListItems;
export const selectStateCodesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.stateCodeListItemsUnfiltered;
export const selectTaskEscalateReasons = (state: { lookup: LookupState }) =>
  state.lookup.taskEscalateReasonItems;
export const selectTaskEscalateReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.taskEscalateReasonItemsUnfiltered;
export const selectTaskResolveReasons = (state: { lookup: LookupState }) =>
  state.lookup.taskResolveReasonItems;
export const selectTaskResolveReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.taskResolveReasonItemsUnfiltered;
export const selectTimeZones = (state: { lookup: LookupState }) =>
  state.lookup.timeZoneItems;
export const selectTimeZonesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.timeZoneItemsUnfiltered;
export const selectTransportationProviderPayStatusesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.transportationProviderPayStatusItemsUnfiltered;
export const selectTripDestinationCategories = (state: {
  lookup: LookupState;
}) => state.lookup.tripDestinationCategoryItems;
export const selectTripDestinationCategoriesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripDestinationCategoryItemsUnfiltered;
export const selectTripLegAdjustmenTypes = (state: { lookup: LookupState }) =>
  state.lookup.tripLegAdjustmentTypeItemsUnfiltered;
export const selectTripLegExceptions = (state: { lookup: LookupState }) =>
  state.lookup.tripLegExceptionItems;
export const selectTripLegExceptionsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegExceptionItemsUnfiltered;
export const selectTripLegPackageRejectReasons = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegPackageRejectReasonItems;
export const selectTripLegPackageRejectReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegPackageRejectReasonItemsUnfiltered;
export const selectTripLegPackageStatusesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegPackageStatusItemsUnfiltered;
export const selectTripLegStatusReasons = (state: { lookup: LookupState }) =>
  state.lookup.tripLegStatusReasonItems;
export const selectTripLegStatusReasonsUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegStatusReasonItemsUnfiltered;
export const selectTripLegStatusesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.tripLegStatusItemsUnfiltered;
export const selectTripModes = (state: { lookup: LookupState }) =>
  state.lookup.tripModeItems;
export const selectTripModesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.tripModeItemsUnfiltered;
export const selectVehicleAttributeTypes = (state: { lookup: LookupState }) =>
  state.lookup.vehicleAttributeTypeItems;
export const selectVehicleAttributeTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.vehicleAttributeTypeItemsUnfiltered;
export const selectVehicleColors = (state: { lookup: LookupState }) =>
  state.lookup.vehicleColorItems;
export const selectVehicleColorsUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.vehicleColorItemsUnfiltered;
export const selectVehicleDocumentTypes = (state: { lookup: LookupState }) =>
  state.lookup.vehicleDocumentTypeItems;
export const selectVehicleDocumentTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.vehicleDocumentTypeItemsUnfiltered;
export const selectVehicleTypes = (state: { lookup: LookupState }) =>
  state.lookup.vehicleTypeItems;
export const selectVehicleTypesUnfiltered = (state: { lookup: LookupState }) =>
  state.lookup.vehicleTypeItemsUnfiltered;
export const selectUserCategoryTypes = (state: { lookup: LookupState }) =>
  state.lookup.userCategoryTypeItems;
export const selectUserCategoryTypesUnfiltered = (state: {
  lookup: LookupState;
}) => state.lookup.userCategoryTypeItemsUnfiltered;
export const selectLookupsApiInfo = (state: { lookup: LookupState }) =>
  state.lookup.lookupsApiInfo;
export const selectTransportationAttributeMap = (state: {
  lookup: LookupState;
}) => state.lookup.transportationAttributeMap;
export const selectReverseTransportationAttributeMap = (state: {
  lookup: LookupState;
}) => state.lookup.reverseTransportationAttributeMap;

export default slice.reducer;
